import { filterClasses } from "@swivl-mono/helpers";
import { type DebugLevel, StructuredActionTaken, StructuredKind } from "@swivl-mono/types";
import React from "react";

import type { ChatMessage } from "../../Types/BotChat.Types";
import { Buttons } from "./Buttons";
import { ImageAttachment } from "./Image";
import { LocationPicker } from "./LocationPicker";
import { ProfileForm } from "./ProfileForm";
import { UnitPicker } from "./UnitPicker";

interface StructuredMessageProps {
  message: ChatMessage;
  onAction?: (action: StructuredActionTaken) => void;
  /** Read-only mode for the agent timeline — disables interactive controls. */
  readOnly?: boolean;
  /** Optional handler for "search wider" affordance on location-picker. */
  onExpandRadius?: () => void;
  /**
   * Push a new `payload.data` for this message after a picker-API filter
   * refresh / zip-entry submit / "Use my location". See widget design doc §A4.6.
   */
  onUpdateData?: (data: Record<string, unknown>) => void;
  /** Org context — required for picker-API calls from picker renderers. */
  orgId?: number;
  /** Conversation id for picker-API debug logging. */
  conversationId?: number | null;
  /**
   * Debug visibility level forwarded to per-kind renderers (e.g. Buttons.tsx
   * surfaces `llmMessage` at `agent`+). Defaults to `"none"` for user
   * widgets; CC V3 forwards the resolved level via prop drill.
   */
  debugLevel?: DebugLevel;
  /**
   * Which role is right-aligned as "mine" — mirrors `MessageBubble.ownRole`, so
   * a card lands on the same side as the bubbles from the same speaker. Only
   * consulted in the agent console (`readOnly`); the widget is unaligned.
   */
  ownRole?: "user" | "assistant";
  /**
   * Resolve the Google Places rating for a `LocationOption` shown in
   * the LocationPicker carousel. Returns null when the operator hasn't
   * enabled Places, the facility has no placeId, or Google has no data.
   * Optional — widgets without Google Places opt-in pass nothing and
   * the carousel skips the rating row.
   */
  fetchPlacesRating?: (locationId: number) => Promise<{ rating: number; userRatingsCount: number } | null>;
}

/**
 * Exhaustive switch on `payload.kind` per protocol §6.4. Adding a kind to
 * `KindRegistry` (in libs/types/src/BotChat) without registering a renderer
 * here is a compile error — `assertNever(kind)` traps it.
 *
 * Runtime fallback (`UnsupportedKind`) handles the case where an old widget
 * version sees a new kind it doesn't yet have a renderer for. Renders as a
 * placeholder rather than crashing the timeline. This is the §6.6 "graceful
 * fallback" behavior — important for post-Stage-4 rollback safety per §6.4.
 */
export function StructuredMessage({
  message,
  onAction,
  readOnly = false,
  onExpandRadius,
  onUpdateData,
  orgId,
  conversationId,
  debugLevel = "none",
  fetchPlacesRating,
  ownRole = "user"
}: StructuredMessageProps): React.ReactElement {
  const payload = message.payload;
  if (!payload) return <UnsupportedKind kind="missing payload" readOnly={readOnly} />;
  const kind = payload.kind as StructuredKind | string;

  // A card the customer has acted on renders as THEIR message (the picked unit,
  // the submitted form); an untouched card is the bot's offer. So the two sides
  // of the same component land on opposite sides of the thread.
  const actionType = (message.actionTaken as { type?: string } | undefined)?.type;
  const isCustomerDecision = actionType === "selected" || actionType === "submitted";
  const botOnRight = ownRole === "assistant";
  // A dismissal isn't either party's message — nobody said anything, the card
  // just went away. So it centres like the timeline's other internal rows
  // instead of picking a side.
  const align: CardAlign = actionType === "dismissed" ? "center" : (isCustomerDecision ? !botOnRight : botOnRight) ? "right" : "left";

  switch (kind) {
    case "location-picker":
      return (
        <ConsoleWidth readOnly={readOnly} align={align}>
          <LocationPicker
            data={payload.data as Parameters<typeof LocationPicker>[0]["data"]}
            message={message}
            onAction={onAction}
            readOnly={readOnly}
            onExpandRadius={onExpandRadius}
            onUpdateData={onUpdateData}
            orgId={orgId}
            conversationId={conversationId}
            fetchPlacesRating={fetchPlacesRating}
          />
        </ConsoleWidth>
      );
    case "unit-picker":
      return (
        <ConsoleWidth readOnly={readOnly} align={align}>
          <UnitPicker
            data={payload.data as Parameters<typeof UnitPicker>[0]["data"]}
            message={message}
            onAction={onAction}
            readOnly={readOnly}
            onUpdateData={onUpdateData}
            orgId={orgId}
            conversationId={conversationId}
          />
        </ConsoleWidth>
      );
    case "buttons":
      return (
        <Buttons
          data={payload.data as Parameters<typeof Buttons>[0]["data"]}
          message={message}
          onAction={onAction}
          readOnly={readOnly}
          debugLevel={debugLevel}
        />
      );
    case "profile-form":
      return (
        <ConsoleWidth readOnly={readOnly} align={align}>
          <ProfileForm data={payload.data as Parameters<typeof ProfileForm>[0]["data"]} message={message} onAction={onAction} readOnly={readOnly} />
        </ConsoleWidth>
      );
    case "image":
      return <ImageAttachment data={payload.data as Parameters<typeof ImageAttachment>[0]["data"]} />;
    default:
      return <UnsupportedKind kind={String(kind)} readOnly={readOnly} />;
  }
}

/**
 * Places and caps the card-shaped kinds in the agent console (`readOnly`). The console
 * panel is far wider than the widget these were designed for, and a card
 * stretched across it reads as a page section rather than one message. The
 * widget passes no `readOnly`, so it is unconstrained as before.
 *
 * Not applied to `buttons` (a wrapping row of chips, which a 420px cap would
 * squeeze into a column) or `image` (sized by its own content).
 */
type CardAlign = "left" | "right" | "center";

const CARD_JUSTIFY: Record<CardAlign, string> = {
  left: "justify-start",
  right: "justify-end",
  center: "justify-center"
};

function ConsoleWidth({ readOnly, align, children }: { readOnly: boolean; align: CardAlign; children: React.ReactNode }): React.ReactElement {
  if (!readOnly) return <>{children}</>;
  return (
    <div className={filterClasses("flex", CARD_JUSTIFY[align])}>
      {/* Same cap as the console bubbles, so a card and a message occupy the
          same column. A sided card fills that width; a centred one is a pill
          that should stay the size of its own text. */}
      <div className={filterClasses("max-w-[min(85%,600px)]", align !== "center" && "w-full")}>{children}</div>
    </div>
  );
}

/**
 * Shown when a card arrives that this build can't render — usually the server
 * started emitting a new kind before the front end shipped it.
 *
 * Console copy leads with what an agent can act on (the customer saw something
 * this view can't show), with the raw kind kept as a secondary line for whoever
 * ends up debugging it. The widget keeps its original terse line: a customer
 * has no use for a kind name, and refreshing genuinely is their best move.
 */
function UnsupportedKind({ kind, readOnly }: { kind: string; readOnly: boolean }): React.ReactElement {
  if (!readOnly) {
    return (
      <div className="rounded-2xl border border-grey-200 bg-grey-50 px-3 py-2 text-xs text-grey-500 italic">
        [unsupported card kind: {kind} — please refresh]
      </div>
    );
  }
  return (
    <div className="flex justify-center">
      <div className="body-mono inline-flex flex-col items-center gap-0.5 rounded-sm bg-grey-50 px-2 py-1 text-center text-grey-600">
        <span>The customer saw a card this view can&apos;t display</span>
        <span className="text-grey-500">{kind}</span>
      </div>
    </div>
  );
}
